import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

/**
 * App-wide offline indicator.
 * - Listens to browser online/offline events.
 * - Shows a red banner while offline.
 * - When connection is restored, briefly shows a green "Back online" toast.
 */
const OfflineBanner = () => {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showBackOnline, setShowBackOnline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBackOnline(true);
      setTimeout(() => setShowBackOnline(false), 3000);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowBackOnline(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[10000] bg-red-600 text-white shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium">
          <WifiOff className="h-4 w-4 flex-shrink-0" />
          <span>No internet connection. Some features may not work until you're back online.</span>
        </div>
      </div>
    );
  }

  if (showBackOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[10000] bg-green-600 text-white shadow-lg animate-fade-in">
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium">
          <Wifi className="h-4 w-4 flex-shrink-0" />
          <span>Back online</span>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineBanner;
